// Light / dark theme for the cinematic public site (side nav, Home, footer,
// 3D scene). Deliberately separate from the app-wide shadcn ThemeProvider so
// admin and the portals keep their own look. The value lives on
// <html data-site-theme>, set before first paint by the inline script in
// index.html; colours come from the --cx-* tokens in index.css.
import { useSyncExternalStore } from "react";
import { fx } from "@/components/fx3d/fxStore";

export type SiteTheme = "light" | "dark";
const KEY = "hibane-site-theme";
const listeners = new Set<() => void>();

const read = (): SiteTheme =>
  typeof document !== "undefined" && document.documentElement.dataset.siteTheme === "light" ? "light" : "dark";

const apply = (t: SiteTheme) => {
  const root = document.documentElement;
  root.dataset.siteTheme = t;
  root.style.colorScheme = t;
  fx.theme = t;
  try {
    localStorage.setItem(KEY, t);
  } catch {
    /* private mode */
  }
  listeners.forEach((l) => l());
};

if (typeof document !== "undefined") fx.theme = read();

/**
 * Switch theme. With an origin point (the switch's centre) and View
 * Transitions support, the new theme grows out of it as a circle.
 */
export function setSiteTheme(t: SiteTheme, origin?: { x: number; y: number }) {
  if (t === read()) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const doc = document as Document & { startViewTransition?: (cb: () => void) => { ready: Promise<void> } };
  if (!reduce && origin && doc.startViewTransition) {
    const r = Math.hypot(Math.max(origin.x, innerWidth - origin.x), Math.max(origin.y, innerHeight - origin.y));
    const vt = doc.startViewTransition(() => apply(t));
    vt.ready
      .then(() =>
        document.documentElement.animate(
          { clipPath: [`circle(0px at ${origin.x}px ${origin.y}px)`, `circle(${r}px at ${origin.x}px ${origin.y}px)`] },
          { duration: 700, easing: "cubic-bezier(0.76, 0, 0.24, 1)", pseudoElement: "::view-transition-new(root)" },
        ),
      )
      .catch(() => {});
    return;
  }
  const root = document.documentElement;
  if (!reduce) {
    root.classList.add("cx-theme-anim");
    window.setTimeout(() => root.classList.remove("cx-theme-anim"), 600);
  }
  apply(t);
}

export const useSiteTheme = () =>
  useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    read,
    () => "dark" as SiteTheme,
  );
